import React from 'react';
import { Image } from '@fluentui/react-components';
import { useNavigate } from 'react-router-dom';
import { IBannerContent, IContentBannerSlide } from '../Helpers/contentSectionConfig';
import { isDarkColor } from '../Helpers/ColorHelpers';
import { textStyleFrom } from '../Helpers/searchPageConfig';
import BannerOverlayCta from './BannerOverlayCta';
import {
    overlayBoxStyleFrom,
    overlayContentStyleFrom,
    overlayFrameStyleFrom,
    overlayFramedLayerStyle,
    overlayLayerStyleFrom,
    parseBannerOverlayConfig,
} from '../Helpers/bannerOverlayConfig';

// A content banner section: a rotating carousel of image slides, each with an optional overlay
// (title/subtitle/CTA). Mirrors the hero banner carousel in HomePage — auto-rotates every 5s, pauses
// while the pointer is over it, crossfades between slides, and shows dot navigation when there is more
// than one active slide. Disabled slides are skipped. Overlay parsing/positioning reuses the shared
// bannerOverlayConfig helpers so a slide's overlay renders exactly like a hero overlay.
//
// A single active slide with no full-bleed renders exactly as a plain banner — it just doesn't rotate.
const ROTATE_MS = 5000;
const CROSSFADE_MS = 700;
const DEFAULT_HEIGHT = 320;

interface BannerSectionProps {
    config: IBannerContent | null;
    // Content column width (settings.maxWidth), used to frame a full-bleed banner's overlay to the
    // content column (like the hero) instead of the full viewport. Defaults to 800 to match HomePage.
    contentMaxWidth?: number;
    // Storefront background, used only to pick a neutral fallback fill behind a fixed-height slide.
    storefrontBackgroundColor?: string;
    // Set when the banner sits in a fractional (packed-row) column. A 100vw full-bleed breakout from
    // inside such a column escapes the column and overlaps its row siblings, so full-bleed is ignored
    // and the banner renders contained. Supplied by ContentSection from section.width.
    disableFullBleed?: boolean;
}

const BannerSection: React.FC<BannerSectionProps> = ({ config, contentMaxWidth, storefrontBackgroundColor, disableFullBleed }) => {
    const nav = useNavigate();
    const [index, setIndex] = React.useState(0);
    // Auto-rotation pauses while the pointer is over the carousel (mirrors a standard hero carousel).
    const [paused, setPaused] = React.useState(false);

    // Active slides: must have an image and not be disabled; dedupe by imageUrl so a repeated image
    // doesn't add a dead dot.
    const slides = React.useMemo(() => {
        const seen = new Set<string>();
        return (config?.banners ?? []).filter(b => {
            if (b.disabled) return false;
            const u = b.imageUrl?.trim();
            if (!u || seen.has(u)) return false;
            seen.add(u);
            return true;
        });
    }, [config]);

    const multiple = slides.length > 1;

    React.useEffect(() => {
        if (!multiple || paused) return;
        const id = setInterval(() => setIndex(prev => (prev + 1) % slides.length), ROTATE_MS);
        return () => clearInterval(id);
    }, [multiple, paused, slides.length]);

    // Keep the index in range if the slide set shrinks (e.g. config change on a live preview).
    React.useEffect(() => {
        if (index > slides.length - 1) setIndex(0);
    }, [slides.length, index]);

    if (slides.length === 0) return null;

    const activeIndex = Math.min(index, slides.length - 1);
    // Inside a fractional column the 100vw breakout would escape the column and overlap the row's
    // other sections, so treat the banner as contained there regardless of the merchant's flag.
    const fullBleed = config?.fullBleed === true && !disableFullBleed;
    const height = `${config?.height ?? DEFAULT_HEIGHT}px`;
    // 'fit' letterboxes against the slide's backgroundColor; default crops to fill the box.
    // Mirrors the admin editor preview (contentSectionConfig.bannerObjectFit).
    const imageObjectFit: 'cover' | 'contain' = config?.imageFit === 'fit' ? 'contain' : 'cover';

    const fallbackBg = storefrontBackgroundColor
        ? (isDarkColor(storefrontBackgroundColor) ? '#2a2a2a' : '#f0f0f0')
        : '#f0f0f0';

    // External URLs open a new tab (noopener/noreferrer so a merchant URL can't get a window handle);
    // internal paths navigate in-app. Shared by the whole-slide click and the overlay CTA.
    const navigateTo = (url?: string) => {
        if (!url) return;
        if (url.startsWith('http')) window.open(url, '_blank', 'noopener,noreferrer');
        else nav(url);
    };

    // Build a slide's overlay node (title/subtitle/CTA), positioned exactly like a hero overlay. A
    // full-bleed banner re-frames the overlay to the content column (centered) so text lines up like a
    // hero; otherwise the overlay fills the banner directly.
    const buildOverlay = (slide: IContentBannerSlide): React.ReactNode => {
        const overlay = parseBannerOverlayConfig(slide.overlayConfig);
        if (!overlay) return null;

        const overlayContent = (
            // The class carries no styling of its own — it's the hook for the narrow-column
            // @container rules in index.css, which relax the 50% content maxWidth and shrink the
            // headline/body type when this banner sits in a skinny fractional column. Those rules
            // only ever match inside a container-type element (the SectionRowLayout column wrapper).
            <div className="sf-banner-overlay-content" style={{ ...overlayContentStyleFrom(overlay.position), ...overlayBoxStyleFrom(overlay) }}>
                {overlay.title?.trim() && (
                    <h2 style={{ margin: 0, fontSize: '2.5rem', fontWeight: 600, lineHeight: 1.2, color: '#1f2933', ...textStyleFrom(overlay.titleStyle, 'heading') }}>
                        {overlay.title}
                    </h2>
                )}
                {overlay.subtitle?.trim() && (
                    <p style={{ margin: 0, fontSize: '1.1rem', color: '#1f2933', ...textStyleFrom(overlay.subtitleStyle, 'body') }}>
                        {overlay.subtitle}
                    </p>
                )}
                {overlay.button?.label?.trim() && (
                    <BannerOverlayCta
                        button={overlay.button}
                        onActivate={() => navigateTo(overlay.button?.url || slide.linkUrl)}
                    />
                )}
            </div>
        );

        return fullBleed
            ? (
                <div style={overlayFramedLayerStyle}>
                    <div style={overlayFrameStyleFrom(overlay.position, contentMaxWidth ?? 800)}>{overlayContent}</div>
                </div>
            )
            : (
                <div style={overlayLayerStyleFrom(overlay.position)}>{overlayContent}</div>
            );
    };

    const dotActiveColor = '#ffffff';
    const dotInactiveColor = 'rgba(255,255,255,0.55)';

    // The active slide's background paints behind the crossfading image layers.
    const boxBg = slides[activeIndex]?.backgroundColor ?? fallbackBg;

    const bannerBox = (
        <div
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            style={{
                position: 'relative',
                width: '100%',
                height,
                minHeight: height,
                maxHeight: height,
                backgroundColor: boxBg,
                borderRadius: fullBleed ? 0 : 8,
                overflow: 'hidden',
                boxSizing: 'border-box',
            }}
        >
            {/* Crossfading layers: all slides stacked absolutely; the active one fades in while the
                previous fades out. Only the active layer is interactive. */}
            {slides.map((slide, i) => {
                const isActive = i === activeIndex;
                return (
                    <div
                        key={slide.imageUrl}
                        style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: isActive ? 1 : 0,
                            transform: isActive ? 'scale(1)' : 'scale(1.02)',
                            transition: `opacity ${CROSSFADE_MS}ms ease, transform ${CROSSFADE_MS}ms ease`,
                            pointerEvents: isActive ? 'auto' : 'none',
                        }}
                    >
                        <Image
                            src={slide.imageUrl}
                            alt={slide.altText}
                            style={{
                                cursor: slide.linkUrl ? 'pointer' : 'default',
                                width: '100%',
                                height: '100%',
                                objectFit: imageObjectFit,
                                margin: 0,
                                display: 'block',
                            }}
                            onClick={() => navigateTo(slide.linkUrl)}
                        />
                        {buildOverlay(slide)}
                    </div>
                );
            })}
            {multiple && (
                <div style={{ position: 'absolute', bottom: '12px', left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: '8px', zIndex: 1 }}>
                    {slides.map((_, i) => (
                        <button
                            key={i}
                            aria-label={`Show banner ${i + 1}`}
                            aria-current={i === activeIndex}
                            onClick={() => setIndex(i)}
                            style={{
                                width: '11px',
                                height: '11px',
                                padding: 0,
                                borderRadius: '50%',
                                border: 'none',
                                cursor: 'pointer',
                                background: i === activeIndex ? dotActiveColor : dotInactiveColor,
                                transform: i === activeIndex ? 'scale(1.2)' : 'none',
                                transition: 'background 0.2s, transform 0.2s',
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );

    // Full-bleed: break out of the max-width content column to the full viewport width. Safe because
    // the layout ancestors clip horizontal overflow (see Layout.tsx). Same technique as HomePage's
    // fullBleedBand, so it composes with the nested sections column identically. ContentSection renders
    // a full-bleed banner WITHOUT its styleProps chrome wrapper so nothing traps this breakout.
    if (fullBleed) {
        return <div style={{ width: '100vw', marginLeft: 'calc(50% - 50vw)', boxSizing: 'border-box' }}>{bannerBox}</div>;
    }
    return bannerBox;
};

export default BannerSection;
